/**
 * The daily cap on INFO reports (#6007).
 *
 * Sentry's monthly event quota ran out in September 2026, and from then on every
 * event was dropped, real crashes included. 85% of the events were INFO reports
 * (`LoggingService.trackSilentInfo`), most of them the same condition repeating on the
 * same device many times a day. So each INFO name now goes to Sentry at most once per
 * device per local calendar day, and the next sent report says how many repeats were
 * held back in between. E2E runs send no INFO to Sentry at all. The measurements and the
 * rules for reading INFO counts from this change on are in `design-docs/telemetry.md`,
 * "INFO reports are sent once per device per day".
 *
 * This module holds the whole INFO decision with its dependencies passed in, so it can
 * be tested without Angular, Sentry or a browser. `LoggingService.trackSilentReport`
 * only wires the real storage, clock and Sentry calls into it.
 */
import { DateTime } from 'luxon';
import { describeError } from '@cf-shared/error-describe';

/** The one `localStorage` key the cap keeps its state under. */
export const INFO_CAP_STORAGE_KEY = 'cs-sentry-info-daily-cap';

/** The Sentry tag every capped INFO event carries. The tunnel (`/api/e`) reads it to tell
 * a capped event from one sent by an app version that has no cap. */
export const INFO_CAP_TAG = 'info_cap';
export const INFO_CAP_TAG_VALUE = 'daily';

/** The INFO report `LoggingService` sends when the cap's storage failed or was repaired. */
export const INFO_CAP_STORAGE_REPORT = 'sentry-info-daily-cap-storage';

/** Entries older than this, holding no repeats, are pruned when the state is written. */
const PRUNE_AFTER_DAYS = 7;

/** One INFO name's record: the last day it was sent, and repeats held back since. */
export interface InfoCapEntry {
  day: string;
  held: number;
}

export type InfoCapState = Record<string, InfoCapEntry>;

/** Access to the stored state. Either call may throw (private mode, quota, blocked storage). */
export interface InfoCapStorage {
  read(): string | null;
  write(value: string): void;
}

/** What the Sentry send did. `sent` is false when the one-minute silencer or `beforeSend`
 * stopped it. */
export interface InfoCapCaptureResult {
  sent: boolean;
  sentryId: string;
}

export enum InfoReportOutcome {
  /** An E2E run: nothing went to Sentry. */
  SkippedE2E = 'skipped-e2e',
  /** Already sent today on this device: counted, not sent. */
  HeldBack = 'held-back',
  /** Sent to Sentry. */
  Sent = 'sent',
  /** A send was attempted but the silencer or `beforeSend` stopped it. */
  NotSent = 'not-sent',
}

export enum InfoCapStorageProblem {
  /** Storage could not be read or written; the cap runs from memory for the session. */
  Unavailable = 'unavailable',
  /** The stored value was damaged and has been repaired. */
  Damaged = 'damaged',
}

export interface InfoReportResult {
  outcome: InfoReportOutcome;
  /** The Sentry id for the console line; empty when nothing was sent. */
  sentryId: string;
  writeConsoleLine: boolean;
  /** Whether the Mixpanel `sentry_error` row may be sent (the caller's own opt-out still applies). */
  sendMixpanelRow: boolean;
  /** Set on the one report where storage first failed or a damaged value was repaired. */
  storageProblem?: { problem: InfoCapStorageProblem, error: string };
}

export interface InfoReportInput {
  name: string;
  context: any;
  isE2E: boolean;
  /** The device's local calendar date, `yyyy-MM-dd`. */
  today: string;
  addBreadcrumb: (data: any) => void;
  capture: (extra: Record<string, unknown>, tags: Record<string, string>) => InfoCapCaptureResult;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Read the stored state, repairing anything of the wrong shape.
 *
 * The value may have been written by a later app version or damaged outright, so it is
 * never trusted: an entry of the wrong shape is dropped (its name then simply sends on
 * its next report), and `damaged` says something was dropped.
 */
export function parseStoredState(raw: string | null): { state: InfoCapState, damaged: boolean, error?: string } {
  // if nothing is stored yet, that is an empty state, not damage
  if (raw === null) {
    return { state: {}, damaged: false };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // the caller reports the damage, carrying this reason
    return { state: {}, damaged: true, error: describeError(err) };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { state: {}, damaged: true };
  }
  const state: InfoCapState = {};
  let damaged = false;
  for (const [name, entry] of Object.entries(parsed as Record<string, unknown>)) {
    const e = entry as Partial<InfoCapEntry> | null;
    // if the entry is well formed, keep it; otherwise drop it and remember the repair
    if (e && typeof e === 'object' && typeof e.day === 'string' && ISO_DAY.test(e.day)
      && Number.isInteger(e.held) && (e.held as number) >= 0) {
      state[name] = { day: e.day, held: e.held as number };
    } else {
      damaged = true;
    }
  }
  return { state, damaged };
}

/** Drop entries older than PRUNE_AFTER_DAYS that hold no repeats. An entry still holding
 * repeats is kept until its next send reports them. */
function prune(state: InfoCapState, today: string): InfoCapState {
  const todayDate = DateTime.fromISO(today);
  const kept: InfoCapState = {};
  for (const [name, entry] of Object.entries(state)) {
    const age = todayDate.diff(DateTime.fromISO(entry.day), 'days').days;
    // if it is old and has nothing left to report, forget it
    if (entry.held === 0 && age > PRUNE_AFTER_DAYS) {
      continue;
    }
    kept[name] = entry;
  }
  return kept;
}

/** The breadcrumb data with the repeat count added. A context that is not a plain object is
 * wrapped, so spreading never turns an array into numbered keys. */
function withRepeats(context: any, repeats: number): Record<string, unknown> {
  const isPlainObject = context && typeof context === 'object' && !Array.isArray(context);
  // if there is no context, the breadcrumb holds only the count
  if (context === undefined || context === null) {
    return { repeatsSinceLastReport: repeats };
  }
  if (isPlainObject) {
    return { ...context, repeatsSinceLastReport: repeats };
  }
  return { context, repeatsSinceLastReport: repeats };
}

/**
 * The cap itself. One instance lives for the session inside `LoggingService`, because once
 * storage has failed the state has to be kept in memory for the rest of the session.
 */
export class InfoDailyCap {
  /** Set once storage has failed; from then on the state lives only here. */
  private memoryState: InfoCapState | undefined;

  constructor(private storage: InfoCapStorage) {}

  /** Decide whether this INFO report is sent, add its breadcrumb, send it if so, and record the outcome. */
  report(input: InfoReportInput): InfoReportResult {
    // if this is an E2E run, keep the breadcrumb and console line but send nothing
    if (input.isE2E) {
      input.addBreadcrumb(input.context);
      return { outcome: InfoReportOutcome.SkippedE2E, sentryId: '', writeConsoleLine: true, sendMixpanelRow: false };
    }

    const loaded = this.load();
    const storageProblem = loaded.problem;
    const state = loaded.state;
    const entry = state[input.name];

    // if this name was already sent today on this device, count the repeat and stop
    if (entry && entry.day === input.today) {
      input.addBreadcrumb(input.context);
      const saveProblem = this.save({ ...state, [input.name]: { day: entry.day, held: entry.held + 1 } }, input.today);
      return {
        outcome: InfoReportOutcome.HeldBack,
        sentryId: '',
        writeConsoleLine: true,
        sendMixpanelRow: false,
        storageProblem: storageProblem ?? saveProblem,
      };
    }

    // otherwise send it; the breadcrumb goes first so the event carries its own context
    const repeats = entry?.held ?? 0;
    input.addBreadcrumb(withRepeats(input.context, repeats));
    const captured = input.capture({ repeatsSinceLastReport: repeats }, { [INFO_CAP_TAG]: INFO_CAP_TAG_VALUE });

    // if the silencer or beforeSend stopped it, leave the state exactly as it was
    if (!captured.sent) {
      return { outcome: InfoReportOutcome.NotSent, sentryId: '', writeConsoleLine: false, sendMixpanelRow: false, storageProblem };
    }
    const saveProblem = this.save({ ...state, [input.name]: { day: input.today, held: 0 } }, input.today);
    return {
      outcome: InfoReportOutcome.Sent,
      sentryId: captured.sentryId,
      writeConsoleLine: true,
      sendMixpanelRow: true,
      storageProblem: storageProblem ?? saveProblem,
    };
  }

  /** Read the state: from memory once storage has failed, otherwise from storage, repairing damage. */
  private load(): { state: InfoCapState, problem?: InfoReportResult['storageProblem'] } {
    // if storage already failed this session, the state lives in memory
    if (this.memoryState) {
      return { state: this.memoryState };
    }
    let raw: string | null;
    try {
      raw = this.storage.read();
    } catch (err) {
      this.memoryState = {};
      return { state: this.memoryState, problem: { problem: InfoCapStorageProblem.Unavailable, error: describeError(err) } };
    }
    const parsed = parseStoredState(raw);
    // if the stored value was damaged, write the repaired state back now, so the damage is
    // reported once even when this report ends up not being sent
    if (parsed.damaged) {
      const writeProblem = this.writeRepaired(parsed.state);
      const state = this.memoryState ?? parsed.state;
      return { state, problem: writeProblem ?? { problem: InfoCapStorageProblem.Damaged, error: parsed.error ?? 'stored value had the wrong shape' } };
    }
    return { state: parsed.state };
  }

  /** Write a repaired state straight back, without pruning, so the damage is reported only once. */
  private writeRepaired(state: InfoCapState): InfoReportResult['storageProblem'] {
    try {
      this.storage.write(JSON.stringify(state));
      return undefined;
    } catch (err) {
      this.memoryState = state;
      return { problem: InfoCapStorageProblem.Unavailable, error: describeError(err) };
    }
  }

  /** Prune and store the state after a report changed it; falls back to memory if storage fails. */
  private save(state: InfoCapState, today: string): InfoReportResult['storageProblem'] {
    const pruned = prune(state, today);
    // if storage already failed this session, keep the state in memory only
    if (this.memoryState) {
      this.memoryState = pruned;
      return undefined;
    }
    try {
      this.storage.write(JSON.stringify(pruned));
      return undefined;
    } catch (err) {
      this.memoryState = pruned;
      return { problem: InfoCapStorageProblem.Unavailable, error: describeError(err) };
    }
  }
}
